import ExcelJS from "exceljs";
import {
  computeAdcapsRemainingDays,
  computeNextAdcapsDueDate,
  formatDateUsMdY,
  parseDateFromBackend,
} from "../../services";
import { CUSTOMER_TYPE, PAY_SOURCE } from "../constants/customer.constant";
import { getSiteDisplayName } from "../constants/siteDisplayNames";
import { formatTbCustomerName } from "./tbSheetExport";
import {
  compareMedicalFormCustomersByName,
  customerNameFields,
} from "./medicalCustomerSheetSave";
import {
  buildMedicalFortuneSheetData,
  downloadMedicalTablePdf,
  ensureExtension,
  triggerBlobDownload,
} from "./medicalSheetCommon";

export const ADCAPS_HEADERS = [
  "No.",
  "Customer",
  "LTSS ID Number",
  "Adcaps Completed Date",
  "Next Adcaps Due Date",
  "Adcaps Remaining Days",
  "ADCAPS Note",
];

const COLUMN_COUNT = ADCAPS_HEADERS.length;
const BASE_COL_WIDTHS_PX = [48, 240, 150, 140, 140, 130, 180];
const BORDER_80 = "FF333333";
export const ADCAPS_YELLOW = "#ffd966";
export const ADCAPS_RED = "#dd7e6b";
const ADCAPS_YELLOW_ARGB = "FFFFD966";
const ADCAPS_RED_ARGB = "FFDD7E6B";
const ADCAPS_YELLOW_RGB = [255, 217, 102];
const ADCAPS_RED_RGB = [221, 126, 107];
const REMAINING_COL = 5;
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const border80 = {
  top: { style: "thin", color: { argb: BORDER_80 } },
  left: { style: "thin", color: { argb: BORDER_80 } },
  bottom: { style: "thin", color: { argb: BORDER_80 } },
  right: { style: "thin", color: { argb: BORDER_80 } },
};

function formatExportedOnDate(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const valid = Number.isNaN(d.getTime()) ? new Date() : d;
  return `${MONTHS[valid.getMonth()]} ${String(valid.getDate()).padStart(2, "0")}, ${valid.getFullYear()}`;
}

export function buildAdcapsTitle(siteNum, exportedAt = new Date()) {
  const site = getSiteDisplayName(siteNum);
  return `${site} Adcaps (Exported on ${formatExportedOnDate(exportedAt)})`;
}

export function isAdcapsRosterCustomer(customer) {
  if (!customer) return false;
  const status = `${customer.status || ""}`.trim().toLowerCase();
  if (status !== "active") return false;
  const type = `${customer.type || ""}`.trim().toLowerCase();
  if (type !== CUSTOMER_TYPE.MEMBER) return false;
  const paySource = `${customer.pay_source || customer.customer_pay_source || ""}`
    .trim()
    .toLowerCase();
  return paySource === PAY_SOURCE.MEDICAID;
}

export function parseAdcapsDate(raw) {
  const text = `${raw ?? ""}`.trim();
  if (!text) return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  }
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  const parsed = parseDateFromBackend(text);
  if (parsed && !Number.isNaN(parsed.getTime())) {
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }
  return null;
}

export function formatAdcapsDate(raw) {
  const parsed = parseAdcapsDate(raw);
  if (!parsed) return `${raw ?? ""}`.trim();
  return formatDateUsMdY(parsed);
}

export function remainingDaysBackground(remainingDays) {
  const text = `${remainingDays ?? ""}`.trim();
  if (!text) return null;
  const n = Number(text);
  if (!Number.isFinite(n)) return null;
  if (n < 0) return ADCAPS_RED;
  if (n < 30) return ADCAPS_YELLOW;
  return null;
}

function remainingDaysSortValue(remainingDays) {
  const text = `${remainingDays ?? ""}`.trim();
  if (!text) return Number.POSITIVE_INFINITY;
  const n = Number(text);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

export function sortAdcapsRows(rows = []) {
  const sorted = [...(rows || [])].sort((a, b) => {
    const diff = remainingDaysSortValue(a.remainingDays) - remainingDaysSortValue(b.remainingDays);
    if (diff !== 0) return diff;
    return compareMedicalFormCustomersByName(a, b);
  });
  return sorted.map((row, index) => ({ ...row, no: index + 1 }));
}

function deriveAdcapsFromCompletedDate(completedDate) {
  const formatted = formatAdcapsDate(completedDate);
  const parsed = parseAdcapsDate(formatted);
  if (!parsed) {
    return {
      completedDate: formatted,
      nextDueDate: "",
      remainingDays: "",
    };
  }
  return {
    completedDate: formatDateUsMdY(parsed),
    nextDueDate: formatDateUsMdY(computeNextAdcapsDueDate(parsed)) || "",
    remainingDays: computeAdcapsRemainingDays(parsed),
  };
}

export function buildAdcapsRows(customers = []) {
  const eligible = (customers || []).filter(isAdcapsRosterCustomer).map((customer) => {
    const derived = deriveAdcapsFromCompletedDate(customer?.adcaps_completed_date);
    return {
      id: `${customer?._id ?? customer?.id ?? ""}`,
      ...customerNameFields(customer),
      name: formatTbCustomerName(customer),
      ltssId: `${customer?.ltss_id_number ?? ""}`.trim(),
      completedDate: derived.completedDate,
      nextDueDate: derived.nextDueDate,
      remainingDays: derived.remainingDays,
      note: `${customer?.adcaps_note ?? ""}`.replace(/\s+/g, " ").trim(),
    };
  });
  return sortAdcapsRows(eligible);
}

export function rowToAdcapsCells(row) {
  return [
    `${row?.no ?? ""}`,
    `${row?.name ?? ""}`,
    `${row?.ltssId ?? ""}`,
    `${row?.completedDate ?? ""}`,
    `${row?.nextDueDate ?? ""}`,
    `${row?.remainingDays ?? ""}`,
    `${row?.note ?? ""}`,
  ];
}

export function applyAdcapsDerivedFields(row) {
  const derived = deriveAdcapsFromCompletedDate(row?.completedDate);
  return {
    ...row,
    completedDate: derived.completedDate,
    nextDueDate: derived.nextDueDate,
    remainingDays: derived.remainingDays,
    note: `${row?.note ?? ""}`.replace(/\s+/g, " ").trim(),
  };
}

export function buildAdcapsSheetModel(rows, siteNum, exportedAt = new Date()) {
  return {
    title: buildAdcapsTitle(siteNum, exportedAt),
    headers: ADCAPS_HEADERS,
    rows: (rows || []).map((row) => rowToAdcapsCells(row)),
    columnWidthsPx: BASE_COL_WIDTHS_PX,
    name: "ADCAPS",
    headerRowHeightPx: 44,
    halignByColumn: {
      0: 0,
      1: 1,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
      6: 1,
    },
    cellBackground: (_rowIndex, colIndex, value) => {
      if (colIndex !== REMAINING_COL) return null;
      return remainingDaysBackground(value);
    },
  };
}

export function buildAdcapsFortuneSheetData(rows, siteNum, exportedAt = new Date()) {
  return buildMedicalFortuneSheetData(buildAdcapsSheetModel(rows, siteNum, exportedAt));
}

function normalizeAdcapsCellText(cell) {
  if (cell == null) return "";
  const raw = typeof cell === "object" ? `${cell.m ?? cell.v ?? ""}` : `${cell}`;
  return `${raw}`.replace(/\s+/g, " ").trim();
}

export function parseAdcapsRowsFromWorkbook(workbook, baselineRows = []) {
  const sheetRows = workbook?.[0]?.data;
  if (!Array.isArray(sheetRows)) return baselineRows;
  const rowCount = baselineRows.length;
  const parsed = [];
  for (let i = 0; i < rowCount; i += 1) {
    const row = sheetRows[i + 2] || [];
    const baseline = baselineRows[i] || {};
    const cells = [];
    for (let c = 0; c < COLUMN_COUNT; c += 1) {
      cells.push(normalizeAdcapsCellText(row[c]));
    }
    parsed.push(
      applyAdcapsDerivedFields({
        id: baseline.id || "",
        no: Number(cells[0]) || baseline.no || i + 1,
        name: baseline.name || "",
        ltssId: cells[2],
        completedDate: cells[3],
        note: cells[6],
      })
    );
  }
  return parsed;
}

export function adcapsRowsEqual(a = [], b = []) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i] || {};
    const right = b[i] || {};
    if (`${left.id ?? ""}` !== `${right.id ?? ""}`) return false;
    if (`${left.ltssId ?? ""}`.trim() !== `${right.ltssId ?? ""}`.trim()) return false;
    if (formatAdcapsDate(left.completedDate) !== formatAdcapsDate(right.completedDate)) {
      return false;
    }
    if (`${left.nextDueDate ?? ""}`.trim() !== `${right.nextDueDate ?? ""}`.trim()) return false;
    if (`${left.remainingDays ?? ""}`.trim() !== `${right.remainingDays ?? ""}`.trim()) return false;
    if (`${left.note ?? ""}`.trim() !== `${right.note ?? ""}`.trim()) return false;
  }
  return true;
}

export function collectAdcapsCustomerUpdates(draftRows = [], baselineRows = []) {
  const byId = new Map((baselineRows || []).map((row) => [`${row.id}`, row]));
  const updates = [];
  (draftRows || []).forEach((row) => {
    const id = `${row?.id ?? ""}`.trim();
    if (!id) return;
    const baseline = byId.get(id);
    if (!baseline) return;
    const ltssId = `${row.ltssId ?? ""}`.trim();
    const completedDate = formatAdcapsDate(row.completedDate);
    const note = `${row.note ?? ""}`.replace(/\s+/g, " ").trim();
    const baselineLtss = `${baseline.ltssId ?? ""}`.trim();
    const baselineDate = formatAdcapsDate(baseline.completedDate);
    const baselineNote = `${baseline.note ?? ""}`.replace(/\s+/g, " ").trim();
    if (ltssId === baselineLtss && completedDate === baselineDate && note === baselineNote) return;
    const payload = { customerId: id };
    if (ltssId !== baselineLtss) payload.ltss_id_number = ltssId;
    if (completedDate !== baselineDate) payload.adcaps_completed_date = completedDate;
    if (note !== baselineNote) payload.adcaps_note = note;
    updates.push(payload);
  });
  return updates;
}

function applyRemainingDaysPdfFill(hookData) {
  if (hookData.section !== "body") return;
  if (hookData.column.index !== REMAINING_COL) return;
  const bg = remainingDaysBackground(hookData.cell.raw);
  if (!bg) return;
  hookData.cell.styles.fillColor = bg === ADCAPS_RED ? ADCAPS_RED_RGB : ADCAPS_YELLOW_RGB;
  hookData.cell.styles.textColor = [0, 0, 0];
}

export async function downloadAdcapsPdf(rows, siteNum, filename = "ADCAPS") {
  const model = buildAdcapsSheetModel(rows, siteNum, new Date());
  await downloadMedicalTablePdf({
    title: model.title,
    headers: model.headers,
    rows: model.rows,
    filename,
    pageOptions: {
      orientation: "landscape",
      fontSize: 10,
      headFontSize: 10,
      titleFontSize: 14,
      headerHeight: 40,
      marginX: 28,
      marginY: 24,
      fillTableWidth: true,
      lineColor: [51, 51, 51],
      didParseCell: applyRemainingDaysPdfFill,
    },
    columnStyles: {
      0: { cellWidth: 32, halign: "center" },
      1: { cellWidth: 180, halign: "left" },
      2: { cellWidth: 120, halign: "center" },
      3: { cellWidth: 100, halign: "center" },
      4: { cellWidth: 100, halign: "center" },
      5: { cellWidth: 90, halign: "center" },
      6: { cellWidth: 114, halign: "left" },
    },
  });
}

export async function downloadAdcapsXlsx(rows, siteNum, filename = "ADCAPS") {
  const model = buildAdcapsSheetModel(rows, siteNum, new Date());
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Worldshine";
  const sheet = workbook.addWorksheet("ADCAPS");
  const colCount = model.headers.length;

  sheet.mergeCells(1, 1, 1, colCount);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = model.title;
  titleCell.font = { bold: true, size: 16, name: "Times New Roman" };
  titleCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  for (let col = 1; col <= colCount; col += 1) {
    sheet.getCell(1, col).border = border80;
  }
  sheet.getRow(1).height = 40;
  sheet.pageSetup = {
    paperSize: 1,
    orientation: "landscape",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    horizontalCentered: true,
  };

  const headerRow = sheet.getRow(2);
  headerRow.height = 36;
  model.headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { bold: true, size: 12, name: "Arial" };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = border80;
  });

  const leftCols = new Set([1, 6]);
  (rows || []).forEach((row, rowIndex) => {
    const excelRow = sheet.getRow(rowIndex + 3);
    rowToAdcapsCells(row).forEach((value, colIndex) => {
      const cell = excelRow.getCell(colIndex + 1);
      if (colIndex === REMAINING_COL && `${value ?? ""}`.trim() !== "") {
        const n = Number(value);
        cell.value = Number.isFinite(n) ? n : value;
      } else {
        cell.value = value || null;
      }
      cell.font = { size: 12, name: "Arial" };
      cell.alignment = {
        horizontal: leftCols.has(colIndex) ? "left" : "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = border80;
      const bg = remainingDaysBackground(colIndex === REMAINING_COL ? value : "");
      if (bg) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: bg === ADCAPS_RED ? ADCAPS_RED_ARGB : ADCAPS_YELLOW_ARGB },
        };
      }
    });
  });

  model.columnWidthsPx.forEach((px, index) => {
    sheet.getColumn(index + 1).width = Math.max(8, Math.round(px / 7));
  });

  const buffer = await workbook.xlsx.writeBuffer();
  triggerBlobDownload(
    buffer,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ensureExtension(filename, ".xlsx")
  );
}
